import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Link, Paper, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { signIn, getCurrentUser, signOut, fetchAuthSession } from "aws-amplify/auth";
import { Button, CountrySelect, FieldWrapper, PageHeader, SwitchToggle } from "@components/ui_v2";
import { TextFieldPassword } from "@components/auth";
import { useAppDispatch } from "@store/store";
import { useLazyGetSessionInfoQuery, useRequestPhoneCodeLoginMutation } from "@api/session";
import { EMAIL, PASSWORD, PASSWORD_ALLOWED_CHARS, PHONE_NUMBER } from "@utils/expreciones";
import { splitPhoneNumber } from "@helpers/funciones";
import { PATHS_AUTH_V2 } from "@helpers/constantes";
import { ApiError } from "@interfaces/index";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

type LoginErrorType = "" | "credentials" | "inactiveRequest";

/** Llave de localStorage donde se recuerda el correo, la misma del login legacy. */
const REMEMBERED_EMAIL_KEY = "email";
/** Llave donde se recuerda el celular (lada + número) cuando la empresa inicia sesión con celular. */
const REMEMBERED_PHONE_KEY = "phone";

const forceSignOut = async () => {
  try {
    await signOut();
  } catch {
    // Sin sesión previa no hay nada que cerrar.
  }
};

/**
 * Login con el diseño v2. Usa exactamente los mismos servicios y el mismo flujo que `Login`
 * (legacy): cierra cualquier sesión previa, `signIn` de Cognito y después:
 * - si Cognito pide contraseña nueva, manda a `/actualizacion_contrasena`;
 * - si el usuario es `client`, pide el código por SMS y manda a `/validacion_codigo_login`;
 * - si no, consulta `session_info` y navega a `current_screen.url`.
 *
 * El usuario es el correo o, si la empresa lo configura (`interfaceConfig.loginType = "phone"`, ej. WuWei), el
 * celular completo con lada (`+525512345678`), con el que se crearon sus cuentas.
 */
export const LoginV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isPhoneLogin = useInterfaceConfig().loginType === "phone";
  const rememberedKey = isPhoneLogin ? REMEMBERED_PHONE_KEY : REMEMBERED_EMAIL_KEY;

  const [triggerGetSessionInfo] = useLazyGetSessionInfoQuery();
  const [requestPhoneCode] = useRequestPhoneCodeLoginMutation();

  const [errorType, setErrorType] = useState<LoginErrorType>("");
  const [rememberUser, setRememberUser] = useState(true);
  const channel = useRef<BroadcastChannel | null>(null);

  /** Recuerda o borra el usuario (correo o celular) según el switch, igual que el legacy al terminar el login. */
  const persistRememberedUser = useCallback(
    (username: string) => {
      if (rememberUser) {
        localStorage.setItem(rememberedKey, username);
      } else if (localStorage.getItem(rememberedKey) === username) {
        localStorage.removeItem(rememberedKey);
      }
    },
    [rememberUser, rememberedKey],
  );

  const formik = useFormik({
    initialValues: { email: "", country_code: "+52", phone: "", password: "" },
    validationSchema: yup.object().shape({
      email: isPhoneLogin
        ? yup.string()
        : yup
            .string()
            .required("Campo requerido")
            .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      phone: isPhoneLogin
        ? yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido")
        : yup.string(),
      password: yup
        .string()
        .required("Campo requerido")
        .test("password_rules", function (value) {
          if (!value) return true;
          if (value.length > 50) {
            return this.createError({ message: "Excede la longitud permitida (50 caracteres)." });
          }
          if (!PASSWORD_ALLOWED_CHARS.test(value)) {
            return this.createError({
              message: "Estás usando caracteres no permitidos. Intenta con otros caracteres.",
            });
          }
          if (!PASSWORD.test(value)) {
            return this.createError({
              message:
                "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
            });
          }
          return true;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await forceSignOut();
        setSubmitting(true);
        setErrorType("");

        const username = isPhoneLogin ? `${values.country_code}${values.phone}` : values.email;
        const signedResult = await signIn({ username, password: values.password });

        if (signedResult?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          navigate("/actualizacion_contrasena", { state: { from: location, email: username } });
          return;
        }

        if (signedResult.isSignedIn && signedResult.nextStep.signInStep === "DONE") {
          localStorage.removeItem("isLoggedOut");
          channel.current?.postMessage("login");

          const session = await fetchAuthSession();
          const payload = session?.tokens?.accessToken?.payload || {};
          const userRole = String(payload["custom:role"] || "");
          const userPhone = String(payload["phone_number"] || "");

          if (userRole === "client") {
            const { phone_number, country_code } = splitPhoneNumber(userPhone);
            await requestPhoneCode({ country_code, phone_number }).unwrap();
            navigate("/validacion_codigo_login");
            return;
          }

          const currentUser = await getCurrentUser();
          const sessionInfo = await triggerGetSessionInfo(currentUser.userId).unwrap();
          persistRememberedUser(username);
          // El backend puede mandar la ruta sin la diagonal inicial; sin ella React Router la encima sobre `/v2/login`.
          const { url } = sessionInfo.data.current_screen;
          navigate(url.startsWith("/") ? url : `/${url}`, { state: { from: location } });
        }
      } catch (error) {
        const api_error = error as ApiError;
        // Mismo mapeo que el legacy: 404 = solicitud inactiva; cualquier otro, credenciales.
        setErrorType(api_error?.status === 404 ? "inactiveRequest" : "credentials");
        // Sin Sentry, igual que el legacy: los errores de Cognito no traen `status` y cada contraseña
        // equivocada se reportaría como incidente.
        dispatch({ type: "CLEAN_STORE" });
        await forceSignOut();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue } = formik;

  useEffect(() => {
    dispatch({ type: "CLEAN_STORE" });
    const remembered = localStorage.getItem(rememberedKey);
    if (!remembered) return;
    if (isPhoneLogin) {
      // Se guarda con lada (`+525512345678`): los últimos 10 dígitos son el número.
      setFieldValue("country_code", remembered.slice(0, -10) || "+52");
      setFieldValue("phone", remembered.slice(-10));
    } else {
      setFieldValue("email", remembered);
    }
  }, [dispatch, setFieldValue, isPhoneLogin, rememberedKey]);

  useEffect(() => {
    channel.current = new BroadcastChannel("session_channel");
    return () => channel.current?.close();
  }, []);

  return (
    <Box>
      <PageHeader eyebrow="Acceso" title="Te damos la bienvenida" subtitle="Ingresa tus datos para iniciar sesión." />

      <Paper variant="lendia_paper">
        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {isPhoneLogin ? (
            <FieldWrapper label="Teléfono celular" error={(formik.touched.phone && formik.errors.phone) || ""}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <CountrySelect
                  value={formik.values.country_code}
                  onChange={(value) => setFieldValue("country_code", value)}
                  disabled={formik.isSubmitting}
                />
                <TextField
                  size="lendia_input"
                  fullWidth
                  name="phone"
                  autoComplete="username"
                  placeholder="55 1234 5678"
                  value={formik.values.phone}
                  onChange={(e) => {
                    const phone = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                    setFieldValue("phone", phone);
                    // Como con el correo: el switch solo queda marcado si es el celular que ya estaba guardado.
                    setRememberUser(
                      `${formik.values.country_code}${phone}` === localStorage.getItem(REMEMBERED_PHONE_KEY),
                    );
                  }}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  inputProps={{ inputMode: "tel", "data-testid": "login-phone-input" }}
                />
              </Box>
            </FieldWrapper>
          ) : (
            <FieldWrapper label="Correo electrónico" error={(formik.touched.email && formik.errors.email) || ""}>
              <TextField
                size="lendia_input"
                fullWidth
                name="email"
                autoComplete="username"
                placeholder="tucorreo@empresa.com"
                value={formik.values.email}
                onChange={(e) => {
                  // Igual que el legacy: el switch solo queda marcado si es el correo que ya estaba guardado.
                  const email = e.target.value.toLowerCase().trim();
                  setFieldValue("email", email);
                  setRememberUser(email === localStorage.getItem(REMEMBERED_EMAIL_KEY));
                }}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
                inputProps={{ "data-testid": "login-email-input" }}
              />
            </FieldWrapper>
          )}

          <FieldWrapper label="Contraseña" error={(formik.touched.password && formik.errors.password) || ""}>
            <TextFieldPassword
              name="password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting}
              testId="login-password-input"
            />
          </FieldWrapper>

          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}
          >
            <SwitchToggle checked={rememberUser} onChange={setRememberUser} label="Recordar mi usuario" />
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => navigate(PATHS_AUTH_V2.recuperarContrasena.path)}
              data-testid="login-forgot-password-link"
              sx={{ color: "primary.main" }}
            >
              <Typography variant="body2" component="span" fontWeight={500}>
                Olvidé mi contraseña
              </Typography>
            </Link>
          </Box>

          {errorType && (
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: 3,
                border: "1px solid",
                borderColor: errorType === "inactiveRequest" ? "warning.light" : "error.light",
                bgcolor: errorType === "inactiveRequest" ? "#FEF4E4" : "#FDECEC",
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                {errorType === "inactiveRequest" ? (
                  <>
                    Tu solicitud no se encuentra activa en este momento. Para continuar con tu trámite, contacta a tu
                    asesor o envía un correo a{" "}
                    <Link href="mailto:soporte@lendia.mx" underline="always" sx={{ color: "text.primary" }}>
                      soporte@lendia.mx
                    </Link>
                    .
                  </>
                ) : (
                  `Credenciales incorrectas. Verifica tu ${isPhoneLogin ? "celular" : "correo electrónico"} y contraseña.`
                )}
              </Typography>
            </Box>
          )}

          <Button
            tone="primary"
            type="submit"
            withArrow
            fullWidth
            disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
            data-testid="login-submit-button"
            sx={{ height: 44 }}
          >
            {formik.isSubmitting ? "Ingresando…" : "Iniciar sesión"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
